import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { WHATSAPP_NUMBER } from './components/socials';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ChatBot from './components/ChatBot';
import Home from './pages/Home';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import BlogNews from './pages/BlogNews';
import About from './pages/About';
import Contact from './pages/Contact';
import Register from './pages/Register';
import Login from './pages/Login';
import UserDashboard from './pages/UserDashboard';
import BuyerRegister from './pages/BuyerRegister';
import BuyerDashboard from './pages/BuyerDashboard';
import MyStore from './pages/MyStore';
import MyPurchases from './pages/MyPurchases';
import Links from './pages/Links';
import Submit from './pages/Submit';
import Team from './pages/Team';
import FAQ from './pages/FAQ';
import ForgotPassword from './pages/ForgotPassword';
import SellerChat from './pages/SellerChat';
import BazaarPage from './pages/bazaar/BazaarPage';
import SellerStorePage from './pages/bazaar/SellerStorePage';
import ProductDetailPage from './pages/bazaar/ProductDetailPage';
import CartPage from './pages/bazaar/CartPage';
import CheckoutPage from './pages/bazaar/CheckoutPage';
import DeliveryPage from './pages/bazaar/DeliveryPage';

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('userToken');
  return token ? children : <Navigate to="/login" replace />;
}

function BuyerProtectedRoute({ children }) {
  const token = localStorage.getItem('buyerToken');
  return token ? children : <Navigate to="/login" replace />;
}

function AnyAuthRoute({ children }) {
  const token = localStorage.getItem('buyerToken') || localStorage.getItem('userToken');
  return token ? children : <Navigate to="/login" replace />;
}

// Routes where Navbar/Footer are hidden
const NO_NAV = ['/login', '/register', '/register/buyer'];

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 1024);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

const WHATSAPP_MSG = 'Hi%20All%20Things!%20I%20have%20a%20question.';

function FloatingMessenger() {
  return (
    <a
      href="https://m.me/61586399832267"
      target="_blank"
      rel="noopener noreferrer"
      title="Chat on Messenger"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #0084FF 0%, #00B2FF 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 30px rgba(0, 132, 255, 0.3)',
        zIndex: 9998,
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 132, 255, 0.4)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 132, 255, 0.3)'; }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.193 14.963l-3.056-3.259-5.963 3.259L10.732 8.2l3.131 3.259L19.752 8.2l-6.559 6.763z"/>
      </svg>
    </a>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Chat on WhatsApp"
      style={{
        position: 'fixed',
        bottom: 100,
        right: 24,
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: '#25D366',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 30px rgba(37, 211, 102, 0.3)',
        zIndex: 9998,
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(37, 211, 102, 0.4)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(37, 211, 102, 0.3)'; }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.372.69 4.588 1.978 6.512L.88 23.12l4.608-1.098A11.946 11.946 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm6.182 16.968c-.386 1.028-1.404 1.72-2.458 1.79-.624.04-1.394.05-2.23-.16-.54-.136-1.102-.33-1.78-.63-2.5-1.11-4.14-3.22-4.73-4.22l-.01-.02c-.36-.62-.66-1.18-.88-1.71-.34-.82-.52-1.52-.62-2.14-.08-.52-.06-.96.06-1.36.12-.4.34-.78.66-1.14.3-.34.64-.48 1.02-.34.1.04.2.08.3.14.1.06.22.14.34.24.28.24.56.56.86.96.18.24.38.5.58.78.12.18.24.36.36.56.08.14.16.28.22.42.04.1.06.2.06.3 0 .14-.06.28-.18.42-.12.14-.26.3-.4.44-.12.12-.22.22-.28.3-.02.02-.04.04-.06.06-.04.04-.06.08-.08.1.06.12.14.28.26.48.14.26.32.54.54.82.44.58 1.06 1.28 1.82 1.88.4.32.8.6 1.2.82.26.14.5.26.72.34.16.06.3.1.42.12.08.02.16.02.22 0 .1-.02.2-.1.34-.24.14-.16.32-.36.52-.6.12-.14.24-.28.38-.4.1-.1.2-.16.32-.18.12-.02.24 0 .38.06.32.12.64.28.98.48.68.4 1.36.84 2.02 1.26.3.2.58.38.82.56.16.12.28.24.38.36.06.08.1.16.12.26.02.1.02.22-.02.36-.06.14-.14.28-.26.42-.12.14-.26.28-.42.4-.2.16-.42.3-.64.4-.28.12-.56.2-.82.24-.2.04-.4.06-.58.06z"/>
      </svg>
    </a>
  );
}

function SiteLayout() {
  const { pathname } = useLocation();
  const hideNav = NO_NAV.includes(pathname);
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  return (
    <div className={!hideNav ? (isMobile ? 'has-topbar' : (sidebarCollapsed ? 'has-sidebar-collapsed' : 'has-sidebar')) : ''} style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
      {!hideNav && <Navbar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />}
      <main>
        <Routes>
          <Route path="/website"     element={<Home />} />
          <Route path="/login"       element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register"    element={<Register />} />
          <Route path="/register/buyer" element={<BuyerRegister />} />
          <Route path="/clients"     element={<Clients />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/blog"        element={<BlogNews />} />
          <Route path="/news"        element={<Navigate to="/blog" replace />} />
          <Route path="/about"       element={<About />} />
          <Route path="/team"        element={<Team />} />
          <Route path="/faq"         element={<FAQ />} />
          <Route path="/contact"     element={<ProtectedRoute><Contact /></ProtectedRoute>} />
          <Route path="/dashboard"   element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
          <Route path="/my-store"    element={<ProtectedRoute><MyStore /></ProtectedRoute>} />
          <Route path="/my-purchases" element={<ProtectedRoute><MyPurchases /></ProtectedRoute>} />
          <Route path="/messages"    element={<ProtectedRoute><SellerChat /></ProtectedRoute>} />
          <Route path="/buyer-dashboard" element={<BuyerProtectedRoute><BuyerDashboard /></BuyerProtectedRoute>} />
          <Route path="/submit"      element={<ProtectedRoute><Submit /></ProtectedRoute>} />
          <Route path="/bazaar"                     element={<BazaarPage />} />
          <Route path="/bazaar/seller/:id"          element={<SellerStorePage />} />
          <Route path="/bazaar/product/:id"         element={<ProductDetailPage />} />
          <Route path="/bazaar/cart/:seller_id"     element={<AnyAuthRoute><CartPage /></AnyAuthRoute>} />
          <Route path="/bazaar/checkout/:seller_id" element={<CheckoutPage />} />
          <Route path="/bazaar/delivery"            element={<BuyerProtectedRoute><DeliveryPage /></BuyerProtectedRoute>} />
          <Route path="*"            element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideNav && <Footer />}
      <ChatBot />
      <FloatingWhatsApp />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    ['user', 'buyer'].forEach(key => {
      const val = localStorage.getItem(key);
      if (val === 'undefined' || val === 'null' || val === '') {
        localStorage.removeItem(key);
      }
    });
  }, []);

  return (
    <BrowserRouter>
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-main)' }}>
        <Routes>
          <Route path="/"   element={<Links />} />
          <Route path="/*"  element={<SiteLayout />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
